'use client'

import { Button } from '@/components/Button/Button'
import { getDiscoveryCoveTicketWithDates } from '@/lib/getTickets'
import useCartStore from '@/store/useCartStore'
import { AddToCartTicketType } from '@/types/CartType'
import { CalendarTicketAvailability, ProductTicketType } from '@/types/TicketType'
import { addMonths, endOfMonth, format, startOfMonth } from 'date-fns'
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react'
import Calendar, { CalendarTileProperties, ViewCallback } from "react-calendar"
import Popup from 'reactjs-popup'
import { getTodaysDate } from '../../../utils/DateUtils'
import { DropDownWithPrice } from '../ListItemTicket/AddtoCartForm/DropDownWithPrice/DropDownWithPrice'
import styles from './CalendarTicketPopUp.module.scss'
import 'react-calendar/dist/Calendar.css';

type CalendarTicketPopUpProps = {
  ticket: ProductTicketType;
  trigger?: string
  qty: number
  setQty: {
    (qty: number): void;
  }
}

async function loadAvailability(
  ticket_id: string,
  date: Date,
  setLoading: Dispatch<SetStateAction<boolean>>,
  setAvailability: Function,
  setSelectedDate: Dispatch<SetStateAction<Date>>,
  setSelectedDatePrice: Dispatch<SetStateAction<string>>
) {
  setLoading(true);

  const numberOfMonthsToFetch = 3;

  const dateFrom = format(addMonths(startOfMonth(date), -numberOfMonthsToFetch), 'yyyy-MM-dd');
  const dateTo = format(endOfMonth(addMonths(startOfMonth(date), numberOfMonthsToFetch)), 'yyyy-MM-dd');

  const tickets = await getDiscoveryCoveTicketWithDates(ticket_id, dateFrom, dateTo);

  if (tickets) {
    setAvailability(tickets.tickets[0].availability);

    const firstAvailableDate =
      tickets.tickets[0].availability?.find(
        (avail) => avail.date >= new Date().toISOString().substring(0, 10),
      ) ?? null;

    if (firstAvailableDate) {
      setSelectedDate(new Date(firstAvailableDate.date));
      setSelectedDatePrice(firstAvailableDate.price);
    }
  } else {
    setAvailability([]);
  }

  setLoading(false);
}

function getAvailabilityForDate(availability: CalendarTicketAvailability[], date: Date) {
  return availability.find((a => a.date === format(date, 'yyyy-MM-dd')))
}


export function CalendarTicketPopUp({ticket, trigger, qty, setQty}: CalendarTicketPopUpProps) {

  const addToCart = useCartStore(state => state.addToCart)
  const cartLoading = useCartStore(state => state.cartLoading)

  const [open, setOpen] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(cartLoading)
  const [availability, setAvailability] : [ availability: CalendarTicketAvailability[], setAvailability: Function ] = useState([])
  const [selectedDate, setSelectedDate] = useState<Date>(new Date())
  const [selectedDatePrice, setSelectedDatePrice] = useState<string>('0');



  useEffect(() => {
    if (open) {
      const startDate = new Date()
      loadAvailability(ticket.product_id, startDate, setLoading, setAvailability, setSelectedDate, setSelectedDatePrice)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[open])

  useEffect(() => {
    const avail = getAvailabilityForDate(availability, selectedDate)
    if (avail) {
      setSelectedDatePrice(avail.price)
    }
  }, [selectedDate, availability])

  const calendarDiv = useRef<HTMLInputElement>(null)

  const getTileDisabled = ({ date }: CalendarTileProperties): boolean => {
    const avail = getAvailabilityForDate(availability, date)
    return avail ? false : true
  }

  const getTileContent = ({ date }: CalendarTileProperties): JSX.Element => {
    const avail = getAvailabilityForDate(availability, date)
    return avail ? <div className='dayPrice'>£{ avail.price }</div> : <></>
  }

  const onChangeMonth: ViewCallback = ({ activeStartDate }) => {
    loadAvailability(ticket.product_id, activeStartDate, setLoading, setAvailability, setSelectedDate, setSelectedDatePrice)
  }

  const selectedDateId = getAvailabilityForDate(availability, selectedDate)?.date_id

  async function addToCartButton(dateId: string, callback?: Function) {

    setLoading(true)

    const items: Array<AddToCartTicketType> = []
    if (ticket) items.push({ ticketId: ticket.ticket_id, dateId, qty: qty})
    if (items.length) await addToCart(items)
    if (callback) callback()
    setQty(0)
    setOpen(false)

    setLoading(false)
  }

  return (
    <>
      <Button onClick={() => setOpen(true)} className={!trigger ? styles.datePicker: styles.btn}>
        {!trigger ? getTodaysDate() : trigger}
      </Button>
      <Popup closeOnDocumentClick open={open} onClose={() => setOpen(false)}>
        <div className='modal'>
          <Button onClick={() => setOpen(false)} text='Close' className='close'/>
          <h3>Select a date:</h3>
          <Calendar
            onChange={setSelectedDate}
            onActiveStartDateChange={onChangeMonth}
            tileDisabled={getTileDisabled}
            tileContent={getTileContent}
            value={selectedDate}
            minDate={new Date()}
            minDetail="month"
            maxDetail='month'
            inputRef={calendarDiv}
          />
          <DropDownWithPrice qty={qty} setQty={setQty} ticketPrice={selectedDatePrice}/>
        
        {selectedDateId ?
          <Button
            loading={loading}
            disabled={qty < 1}
            onClick={() => addToCartButton(selectedDateId, () => setOpen(false))}
            className={styles.btn}
          >
            Add to Cart
          </Button>
          :
          null
        }
        </div>
      </Popup>
    </>
  )
}